import { FormProvider, useForm } from "react-hook-form"
import { original } from "@reduxjs/toolkit"
import { CommentsSection } from "../../CommentsSection"
import { CommentsType } from "../../../types/common"
import {
    projectsApi,
    useAddCommentToProjectMutation,
    useGetCommentsQuery,
    useGetProjectQuery,
    useLazyGetCommentsQuery,
    useUpdateCommentMutation,
    useDeleteCommentMutation,
    useGetProfileQuery,
} from "../../../api"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react"
import { findCommentInTree } from "../../CommentsSection/helpers/findCommentInTree"
import { resolveThreadAnchorId } from "../../CommentsSection/helpers/resolveThreadAnchorL2"
import { HIGHLIGHT_HOLD_MS } from "../../CommentsSection/helpers/commentHighlightSx"
import modalEmitter from "../../ModalComponent/modalEmitter"
import { CommentThreadModalBody } from "../../Modals/CommentThreadModalBody"
import { CommentDeleteConfirmModalBody } from "../../Modals/CommentDeleteConfirmModalBody"
import type { SubmitCommentDetailedResult } from "../../Modals/CommentThreadModalBody"
import { useDispatch } from "react-redux"
import type { AppDispatch } from "../../../store/store"
import { DEFAULT_COMMENTS_SORT } from "../../CommentsSection/helpers/commentsSortQuery"
import {
    mergeChildrenIntoParent,
    mergeNewCommentIntoCache,
    patchCommentTextInTree,
    removeCommentFromTree,
    sortCommentsByPublishedAtAsc,
} from "../../CommentsSection/helpers/mergeCommentBranch"
import type { OpenL2ReplyThreadPayload } from "../../CommentsSection/ChildrenComponents/ReplyItem"
import {
    resolveProjectCommentTabCounts,
    type CommentsSortUi,
    type IProjectCommentCountersByType,
} from "../../../api/types/projects"
import { CURSOR_LIST_PAGE_SIZE } from "../../../consts/api"

function incrementProjectCountersForCreatedComment(commentsField: unknown, createdType: string | undefined): void {
    if (!commentsField || typeof commentsField !== "object") return
    const c = commentsField as IProjectCommentCountersByType
    c.total = (Number(c.total) || 0) + 1
    const t = (createdType ?? "").toLowerCase()
    if (t === "opinion") c.opinions = (Number(c.opinions) || 0) + 1
    else if (t === "suggestion") c.suggestions = (Number(c.suggestions) || 0) + 1
    else if (t === "question") c.questions = (Number(c.questions) || 0) + 1
}

type CommentsWrapperProps = {
    onClose?: () => void
    initialCommentTab?: CommentsType
    notificationParentCommentId?: number
    notificationCommentId?: number
    onNotificationDeepLinkConsumed?: () => void
    commentsSectionRootRef?: RefObject<HTMLElement | null>
}

type CommentsFormValues = {
    commentText: string
    replyText: string
    filterType: CommentsType
    sort: CommentsSortUi
    mobileReplyTarget: { id: number; name: string } | null
}

export const CommentsWrapper = ({
    onClose,
    initialCommentTab,
    notificationParentCommentId,
    notificationCommentId,
    onNotificationDeepLinkConsumed,
    commentsSectionRootRef,
}: CommentsWrapperProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const [fetchCommentBranch] = useLazyGetCommentsQuery()
    const [loadingParentId, setLoadingParentId] = useState<number | null>(null)
    const [highlightCommentId, setHighlightCommentId] = useState<number | null>(null)
    const notificationBranchLoadedRef = useRef<number | null>(null)
    const notificationScrollDoneRef = useRef(false)
    const { slug } = useParams<{ slug: string }>()

    const methods = useForm<CommentsFormValues>({
        defaultValues: {
            commentText: "",
            replyText: "",
            sort: DEFAULT_COMMENTS_SORT,
            filterType: initialCommentTab ?? CommentsType.opinions,
            mobileReplyTarget: null as { id: number; name: string } | null,
        },
    })

    useEffect(() => {
        if (initialCommentTab) {
            methods.setValue("filterType", initialCommentTab)
        }
    }, [initialCommentTab, methods])

    useEffect(() => {
        notificationBranchLoadedRef.current = null
        notificationScrollDoneRef.current = false
    }, [notificationParentCommentId, notificationCommentId, slug])

    const currentFilter = methods.watch("filterType") as CommentsType
    const currentSort = methods.watch("sort") as CommentsSortUi
    const { data: projectData } = useGetProjectQuery(slug ?? "")
    const projectId = projectData?.id
    const tabCounts = useMemo(
        () => resolveProjectCommentTabCounts(projectData?.counters?.comments),
        [projectData?.counters?.comments]
    )
    const { data: profile } = useGetProfileQuery()
    const currentUserId = profile?.id ?? null
    const [addCommentToProject, { isLoading: isSubmittingComment }] = useAddCommentToProjectMutation()
    const [updateComment, { isLoading: isUpdatingComment }] = useUpdateCommentMutation()
    const [deleteCommentMutation] = useDeleteCommentMutation()
    const canComment = projectData?.viewer?.permissions?.can_comment !== false

    const commentsListCacheKey = useMemo(
        () => ({
            projectId: projectId ?? 0,
            type: currentFilter,
            limit: CURSOR_LIST_PAGE_SIZE,
            sort: currentSort,
        }),
        [projectId, currentFilter, currentSort],
    )

    const { data: commentsData, isLoading: isCommentsLoading, isError: isCommentsError } = useGetCommentsQuery(
        commentsListCacheKey,
        {
            skip: !projectId,
        }
    )

    const submitCommentDetailed = useCallback(
        async ({
            text,
            parentId,
        }: {
            text: string
            parentId?: number
        }): Promise<SubmitCommentDetailedResult> => {
            const trimmed = text.trim()
            if (!trimmed || !projectId) return { ok: false }

            const body = parentId ? { text: trimmed, parent_id: parentId } : { text: trimmed }
            try {
                const created = await addCommentToProject({
                    projectId,
                    type: currentFilter,
                    body,
                }).unwrap()

                dispatch(
                    projectsApi.util.updateQueryData(
                        "getComments",
                        commentsListCacheKey,
                        (draft) =>
                            mergeNewCommentIntoCache(
                                original(draft) ?? [...draft],
                                created,
                                created.parent_id ?? null,
                                currentSort,
                            )
                    )
                )

                if (slug) {
                    dispatch(
                        projectsApi.util.updateQueryData("getProject", slug, (draft) => {
                            incrementProjectCountersForCreatedComment(draft.counters.comments, created.type)
                        })
                    )
                }

                if (parentId) {
                    methods.setValue("replyText", "")
                    methods.setValue("mobileReplyTarget", null)
                    methods.setValue("commentText", "")
                } else {
                    methods.setValue("commentText", "")
                }
                return { ok: true, created }
            } catch {
                toast.error("Не удалось отправить комментарий")
                return { ok: false }
            }
        },
        [projectId, currentFilter, currentSort, slug, addCommentToProject, dispatch, methods, commentsListCacheKey]
    )

    const onSubmitComment = async ({ text, parentId }: { text: string; parentId?: number }): Promise<boolean> => {
        const r = await submitCommentDetailed({ text, parentId })
        return r.ok
    }

    const onUpdateComment = useCallback(
        async (commentId: number, text: string): Promise<boolean> => {
            if (!projectId) return false
            try {
                const updated = await updateComment({ commentId, body: { text } }).unwrap()
                dispatch(
                    projectsApi.util.updateQueryData(
                        "getComments",
                        commentsListCacheKey,
                        (draft) =>
                            patchCommentTextInTree(original(draft) ?? [...draft], commentId, updated)
                    )
                )
                return true
            } catch {
                toast.error("Не удалось сохранить изменения")
                return false
            }
        },
        [updateComment, dispatch, projectId, commentsListCacheKey]
    )

    const onDeleteComment = useCallback(
        (commentId: number): Promise<boolean> =>
            new Promise((resolve) => {
                let settled = false
                const settle = (value: boolean) => {
                    if (settled) return
                    settled = true
                    resolve(value)
                }

                const performDelete = async (): Promise<boolean> => {
                    try {
                        await deleteCommentMutation({ commentId }).unwrap()
                        dispatch(
                            projectsApi.util.updateQueryData(
                                "getComments",
                                commentsListCacheKey,
                                (draft) =>
                                    removeCommentFromTree(original(draft) ?? [...draft], commentId)
                            )
                        )
                        toast.success("Комментарий удалён")
                        settle(true)
                        return true
                    } catch {
                        toast.error("Не удалось удалить комментарий")
                        return false
                    }
                }

                modalEmitter.emitOpenModal({
                    dialog: {
                        title: "Удалить комментарий?",
                        body: (
                            <CommentDeleteConfirmModalBody
                                onConfirm={performDelete}
                                onCancel={() => settle(false)}
                            />
                        ),
                        modalToCentrPosition: true,
                        afterCloseCallBack: () => settle(false),
                    },
                    closeAtion: { visible: false },
                    confirmAtion: { visible: false },
                })
            }),
        [deleteCommentMutation, dispatch, commentsListCacheKey]
    )

    const handleOpenReplyThread = useCallback(
        (p: OpenL2ReplyThreadPayload) => {
            if (!projectId) return
            const initialComposerTarget =
                p.startReplyComposer === true
                    ? {
                          parentId: p.anchorCommentId,
                          firstName: p.anchorFirstName ?? "",
                          lastName: p.anchorLastName ?? "",
                      }
                    : undefined
            modalEmitter.emitOpenModal({
                dialog: {
                    title: "Ответы",
                    body: (
                        <CommentThreadModalBody
                            projectId={projectId}
                            commentType={currentFilter}
                            anchorL2Id={p.anchorCommentId}
                            excerpt={p.excerpt}
                            currentUserId={currentUserId}
                            submitCommentDetailed={submitCommentDetailed}
                            onUpdateComment={onUpdateComment}
                            isUpdatingComment={isUpdatingComment}
                            isSubmittingComment={isSubmittingComment}
                            initialComposerTarget={initialComposerTarget}
                            highlightCommentId={p.highlightCommentId}
                        />
                    ),
                    tallCentered: true,
                },
                closeAtion: { visible: false },
                confirmAtion: { visible: false },
            })
        },
        [
            projectId,
            currentFilter,
            currentUserId,
            submitCommentDetailed,
            isUpdatingComment,
            isSubmittingComment,
            onUpdateComment,
        ]
    )

    const loadMoreReplies = useCallback(
        async (parentId: number) => {
            if (!projectId) return
            setLoadingParentId(parentId)
            try {
                const newNodes = await fetchCommentBranch({
                    projectId,
                    type: currentFilter,
                    parentId,
                    limit: CURSOR_LIST_PAGE_SIZE,
                }).unwrap()
                const sortedNewNodes = sortCommentsByPublishedAtAsc(newNodes)

                dispatch(
                    projectsApi.util.updateQueryData(
                        "getComments",
                        commentsListCacheKey,
                        (draft) =>
                            mergeChildrenIntoParent(
                                original(draft) ?? [...draft],
                                parentId,
                                sortedNewNodes
                            )
                    )
                )
            } catch {
                toast.error("Не удалось загрузить ответы")
            } finally {
                setLoadingParentId(null)
            }
        },
        [projectId, currentFilter, dispatch, fetchCommentBranch, commentsListCacheKey]
    )

    useEffect(() => {
        if (!notificationParentCommentId) return
        if (notificationBranchLoadedRef.current === notificationParentCommentId) return
        if (isCommentsLoading || !commentsData) return

        const parentId = notificationParentCommentId
        if (loadingParentId === parentId) return

        const found = findCommentInTree(commentsData, parentId)
        const totalChildren = found?.counters?.children ?? 0
        const shownChildren = found?.children?.length ?? 0
        const needsLoad = !found || totalChildren > shownChildren

        if (needsLoad) {
            void loadMoreReplies(parentId).finally(() => {
                notificationBranchLoadedRef.current = parentId
            })
        } else {
            notificationBranchLoadedRef.current = parentId
        }
    }, [
        notificationParentCommentId,
        isCommentsLoading,
        commentsData,
        loadMoreReplies,
        loadingParentId,
    ])

    useEffect(() => {
        if (!notificationCommentId || !onNotificationDeepLinkConsumed) return
        if (notificationScrollDoneRef.current) return
        if (isCommentsLoading || !commentsData) return
        if (loadingParentId !== null) return

        if (notificationParentCommentId) {
            const parentBranchDone =
                notificationBranchLoadedRef.current === notificationParentCommentId &&
                loadingParentId === null
            if (!parentBranchDone) return
        }

        const targetId = notificationCommentId
        const el = commentsSectionRootRef?.current
            ? commentsSectionRootRef.current.querySelector<HTMLElement>(`#comment-${targetId}`)
            : document.getElementById(`comment-${targetId}`)

        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" })
            setHighlightCommentId(targetId)
            notificationScrollDoneRef.current = true
            onNotificationDeepLinkConsumed()
            return
        }

        const anchorId =
            (notificationParentCommentId != null
                ? resolveThreadAnchorId(commentsData, notificationParentCommentId)
                : null) ??
            resolveThreadAnchorId(commentsData, targetId) ??
            null

        if (anchorId != null) {
            const anchorNode = findCommentInTree(commentsData, anchorId)
            handleOpenReplyThread({
                anchorCommentId: anchorId,
                excerpt: anchorNode?.text ?? "",
                replyCount: anchorNode?.counters?.children ?? 0,
                highlightCommentId: targetId,
            })
            notificationScrollDoneRef.current = true
            onNotificationDeepLinkConsumed()
            return
        }

        toast.error("Комментарий не найден")
        notificationScrollDoneRef.current = true
        onNotificationDeepLinkConsumed()
    }, [
        notificationCommentId,
        notificationParentCommentId,
        onNotificationDeepLinkConsumed,
        isCommentsLoading,
        commentsData,
        loadingParentId,
        handleOpenReplyThread,
        commentsSectionRootRef,
    ])

    useEffect(() => {
        if (highlightCommentId == null) return
        const t = window.setTimeout(() => setHighlightCommentId(null), HIGHLIGHT_HOLD_MS)
        return () => window.clearTimeout(t)
    }, [highlightCommentId])

    return (
        <FormProvider {...methods}>
           <CommentsSection
               onClose={onClose}
               highlightCommentId={highlightCommentId}
               commentsData={commentsData}
               onSubmitComment={onSubmitComment}
               isSubmittingComment={isSubmittingComment}
               isCommentsLoading={isCommentsLoading}
               isCommentsError={isCommentsError}
               loadMoreReplies={loadMoreReplies}
               loadingParentId={loadingParentId}
               currentUserId={currentUserId}
               onUpdateComment={onUpdateComment}
               isUpdatingComment={isUpdatingComment}
               onDeleteComment={onDeleteComment}
               canComment={canComment}
               onOpenReplyThread={handleOpenReplyThread}
               tabCounts={tabCounts}
               commentReactionCache={
                   projectId
                       ? { projectId, type: currentFilter, sort: currentSort }
                       : undefined
               }
           />
        </FormProvider>
    )
}
